import { FC, memo, useEffect, useState } from 'react';

import Link from 'next/link';
import router from 'next/router';

import ScrollableIcon from '/public/images/iqfu/public/scrollable_icon.svg';

import { RegionDataList, PrefecturesListWithoutSuffix, PublicPropertyType } from '@constants_iqfu';
import { CompaniesPageQueryType } from 'src/iqfu/data/companiesPagesData';

import styles from './PrefecturesLinkList.module.scss';

type CompanyPageProps = {
  category: 'company';
  pageQueryType: CompaniesPageQueryType;
  hasReadMoreButton?: boolean;
  location?: string;
};

type PriceDataList = {
  average_price: number;
  code: number;
}[];

type MansionPricePageProps = {
  category: 'mansion';
  mansionRegionsAveragePrices: PriceDataList | undefined;
  hasReadMoreButton?: boolean;
  location?: string;
};

type LandHousePricePageProps = {
  category: 'landhouse';
  houseRegionsAveragePrices: PriceDataList | undefined;
  landRegionsAveragePrices: PriceDataList | undefined;
  hasReadMoreButton?: boolean;
  location?: string;
};

type Props = CompanyPageProps | MansionPricePageProps | LandHousePricePageProps;

const PrefecturesLinkList: FC<Props> = memo((props) => {
  const { category, hasReadMoreButton = false, location } = props;

  const propertyTypeLabel = category === 'mansion' ? 'マンション' : '土地戸建';

  const [readMoreButtonClicked, setReadMoreButtonClicked] = useState(false);

  useEffect(() => {
    setReadMoreButtonClicked(false);
  }, [router]);

  return (
    <>
      <div
        className={`${styles.list_container} ${hasReadMoreButton ? styles.hasReadMoreButton : ''} ${
          readMoreButtonClicked ? styles.isScrollable : ''
        }`}
      >
        {(Object.keys(RegionDataList) as unknown as (keyof typeof RegionDataList)[]).map((regionCode) => {
          return (
            <div className={styles.area_group} key={regionCode}>
              <div className={styles.area_group_title}>
                {category === 'company' ? (
                  <h3>
                    <span className={styles.area_name}>{RegionDataList[regionCode].name}</span>
                    の不動産屋さんランキングを見る
                  </h3>
                ) : (
                  <>
                    <h3>
                      <span className={styles.area_name}>{RegionDataList[regionCode].name}</span>の{propertyTypeLabel}
                      <br className={'sp_only'} />
                      の売却査定額
                    </h3>
                    {category === 'mansion' ? (
                      <>
                        {props.mansionRegionsAveragePrices && (
                          <AveragePrice
                            priceDataList={props.mansionRegionsAveragePrices}
                            regionCode={Number(regionCode)}
                            propertyType={'mansion'}
                            category={category}
                          />
                        )}
                      </>
                    ) : (
                      <div className={styles.landHouses_average_price_area}>
                        {props.houseRegionsAveragePrices && (
                          <AveragePrice
                            priceDataList={props.houseRegionsAveragePrices}
                            regionCode={Number(regionCode)}
                            propertyType={'house'}
                            category={category}
                          />
                        )}
                        {props.landRegionsAveragePrices && (
                          <AveragePrice
                            priceDataList={props.landRegionsAveragePrices}
                            regionCode={Number(regionCode)}
                            propertyType={'land'}
                            category={category}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <ul className={styles.link_list}>
                {RegionDataList[regionCode].prefectures.map((prefectureNumber) => {
                  return (
                    <li className={`${styles.link_item}`} key={prefectureNumber}>
                      <Link
                        prefetch={false}
                        href={
                          category === 'company'
                            ? `/companies/prefectures/${prefectureNumber}/${props.pageQueryType}`
                            : category === 'mansion'
                            ? `/mansions/prefectures/${prefectureNumber}`
                            : `/landhouses/prefectures/${prefectureNumber}`
                        }
                        passHref
                        className={styles.prefLink}
                        data-gtm-click={`PrefecturesLinkList_item_prefecture_links_${location}`}
                      >
                        {PrefecturesListWithoutSuffix[prefectureNumber]}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {hasReadMoreButton && (
          <div className={`${styles.read_more_container} ${readMoreButtonClicked ? styles.isClicked : ''}`}>
            <p className={styles.scrollable_text}>
              <ScrollableIcon />
              スクロール出来るようになります
            </p>
            <button
              className={`${styles.read_more_button} ${readMoreButtonClicked ? styles.isClicked : ''}`}
              onClick={() => setReadMoreButtonClicked(true)}
            >
              もっと見る
            </button>
          </div>
        )}
      </div>
    </>
  );
});

PrefecturesLinkList.displayName = 'PrefecturesLinkList';

type AveragePriceProps = {
  priceDataList: PriceDataList;
  regionCode: number;
  propertyType: keyof typeof PublicPropertyType;
  category?: 'mansion' | 'landhouse';
};

const AveragePrice: FC<AveragePriceProps> = memo(({ priceDataList, regionCode, propertyType, category }) => {
  const price = priceDataList.find((priceData) => priceData.code === Number(regionCode))?.average_price;
  const prefixText = { land: '土地：', house: '戸建：', mansion: '' };

  return (
    <div
      className={`${category === 'mansion' ? styles.mansion_average_text : styles.landHouses_average_text} ${
        price ? '' : styles.empty_price
      }`}
    >
      {prefixText[propertyType]}平均{price}万円/m2
    </div>
  );
});

AveragePrice.displayName = 'AveragePrice';

export default PrefecturesLinkList;
